import type { TranslationKeys } from '../types';

/**
 * English (US) translations
 */
export const enUS: TranslationKeys = {
  language: {
    cmn: 'Chinese',
    eng: 'English',
    jpn: 'Japanese',
    kor: 'Korean',
    spa: 'Spanish',
    fra: 'French',
    deu: 'German',
    rus: 'Russian',
    por: 'Portuguese',
    ita: 'Italian',
    yue: 'Cantonese',
    nld: 'Dutch',
    arb: 'Standard Arabic',
    ind: 'Indonesian',
  },

  // Common
  common: {
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    retry: 'Retry',
    refresh: 'Refresh',
    saving: 'Saving...',
    processing: 'Processing...',
    understood: 'Understood',
    popular: 'Popular Languages',
    tokenNotEnough: 'Token balance is insufficient. Please purchase more tokens or upgrade your subscription'
  },

  // Navigation
  nav: {
    home: 'Home',
    translation: 'Real-time Translation',
    history: 'Conversation History',
    settings: 'Settings',
    help: 'Help',
    about: 'About',
    profile: 'Profile',
    logout: 'Logout',
  },

  // Translation features
  translation: {
    startTranslation: 'Start Translation',
    stopTranslation: 'Stop Translation',
    stopping: 'Stopping...',
    realTimeTranslation: 'Real-time Translation',
    sourceLanguage: 'Source Language',
    targetLanguage: 'Target Language',
    swapLanguages: 'Swap Languages',
    selectLanguage: 'Select Language',
    selectLanguageDescription :'Choose two languages to translate',
    detectLanguage: 'Auto Detect',
    translating: 'Translating...',
    translationComplete: 'Translation Complete',
    translationError: 'Translation Failed',
    noTranslationYet: 'No translation yet',
    speakNow: 'Please start speaking...',
    listening: 'Listening...',
    processing: 'Processing...',
    sameLanguageError: 'Source and target languages cannot be the same. Please select different languages.',
  },

  // Audio/Microphone
  audio: {
    microphonePermission: 'Microphone Permission',
    microphonePermissionDenied: 'Microphone permission denied',
    selectMicrophone: 'Select Microphone',
    noMicrophoneFound: 'No microphone found',
    microphoneError: 'Microphone error',
    requestPermission: 'Please grant microphone permission',
    permissionGranted: 'Permission granted',
    permissionRequired: 'Please grant microphone permission to start translation',
  },

  // Settings
  settings: {
    title: 'Settings',
    language: 'Language Settings',
    uiLanguage: 'Interface Language',
    theme: 'Theme',
    audio: 'Audio Settings',
    account: 'Account Settings',
    privacy: 'Privacy Settings',
    about: 'About App',
    version: 'Version Info',
    description: 'Configure application options',
    profile: 'Profile',
    security: 'Security',
    dangerZone: 'Danger Zone',
    pleaseLogin: 'Please login to view account settings',
  },

  // Profile settings
  profile: {
    displayName: 'Display Name',
    email: 'Email Address',
    emailNotifications: 'Email Notifications',
    emailNotificationsDesc: 'Receive important updates and notifications via email',
    memberSince: 'Member Since',
    enterName: 'Please enter your name',
    nameRequired: 'Name is required',
    updateSuccess: 'Profile updated successfully',
    updateFailed: 'Failed to update profile',
    avatarUploadComingSoon: 'Avatar upload feature coming soon',
  },

  // Security settings
  security: {
    twoFactorAuth: 'Two-Factor Authentication',
    mfaDescription: 'Add an extra layer of security to your account',
    enableMfa: 'Enable Two-Factor Authentication',
    disable: 'Disable',
    enable: 'Enable',
    enabled: 'Enabled',
    mfaEnabled: 'Two-factor authentication enabled',
    mfaDisabled: 'Two-factor authentication disabled',
    mfaUpdateFailed: 'Failed to update two-factor authentication',
    socialLoginMfaWarning: 'Two-factor authentication is not available for social login accounts',
    socialLoginMfaWarningDesc: 'Security settings for social login accounts are managed by the third-party provider',
    password: 'Password',
    changePassword: 'Change Password',
    passwordDescription: 'Regularly update your password to keep your account secure',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    updatePassword: 'Update Password',
    passwordChanged: 'Password changed successfully',
    passwordChangeFailed: 'Failed to change password',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    loginHistory: 'Login History',
    lastLogin: 'Last Login',
    never: 'Never',
    accountCreated: 'Account Created',
    loginMethod: 'Login Method',
    socialLogin: 'Social Login',
    socialLoginConnected: 'Connected with {provider}',
    socialLoginNote: 'Note',
    socialLoginNoteDesc: 'You are using {provider} social login. Password and security settings are managed by {provider}',
    mfaNotAvailable: 'Two-Factor Authentication Not Available',
    mfaNotAvailableTitle: 'Not available for social login accounts',
    mfaNotAvailableDesc: 'Security settings for social login accounts are managed by the third-party provider and cannot be configured here',
    mfaModalDescription: 'Enable two-factor authentication to enhance your account security',
    mfaWillProvide: 'Two-factor authentication will provide:',
    mfaBenefit1: 'Additional account security protection',
    mfaBenefit2: 'Prevention of unauthorized access',
    mfaBenefit3: 'Compliance with security best practices',
    mfaWarning: 'Once enabled, you will need to provide a verification code every time you log in',
    logoutAllDevices: 'Logout All Devices',
    logoutAllDevicesDesc: 'Force logout from all logged-in devices and sessions',
    logout: 'Logout',
    delete: 'Delete',
    deleteAccount: 'Delete Account',
    deleteAccountDesc: 'Permanently delete your account and all associated data',
    confirmLogout: 'Confirm Logout',
    confirmLogoutDesc: 'This will log you out from all devices and you will need to log in again',
    confirmDelete: 'Confirm Account Deletion',
    confirmDeleteDesc: 'This action cannot be undone and will permanently delete your account and all data',
    deleteWarning: 'Warning: This action cannot be undone',
    deleteWarning1: 'All personal data will be permanently deleted',
    deleteWarning2: 'Conversation history and settings will be lost',
    deleteWarning3: 'Deleted accounts cannot be recovered',
    deleteConfirmLabel: 'Type the following text to confirm deletion',
    logoutSuccess: 'Successfully logged out from all devices',
    logoutFailed: 'Logout failed, please try again',
    accountDeleted: 'Account successfully deleted',
    accountDeleteFailed: 'Failed to delete account, please try again',
    deleteConfirmError: 'Please enter the correct confirmation text',
  },

  // Errors
  errors: {
    networkError: 'Network connection error',
    serviceUnavailable: 'Service temporarily unavailable',
    translationFailed: 'Translation failed, please try again',
    audioError: 'Audio device error',
    permissionError: 'Permission error',
    unknownError: 'Unknown error',
    tryAgain: 'Please try again',
  },

  // Home page
  home: {
    title: 'Lingo AI Translator',
    subtitle: 'Smooth Real-time Translation',
    description: 'Real-time voice translation supporting multiple languages for seamless communication',
    getStarted: 'Get Started',
    features: {
      realTime: 'Real-time Translation',
      multiLanguage: 'Multi-language Support',
      highQuality: 'High Quality Translation',
    },
  },

  // Free trial
  freeTrial: {
    title: 'Trial Mode',
    description: 'Login for full features',
    tokensRemaining: 'Tokens Remaining',
    timeRemaining: 'Time Remaining',
    upgradeNow: 'Upgrade Now',
    trialExpired: 'Trial Expired',
    trialEnded: 'Trial Ended',
    trialUnavailable: 'Free Trial Unavailable',
    getOfficialAccount: 'Get Official Account',
    loginForMore: 'Login for More',
    expired: 'Expired',
    tokensExhausted: 'Trial tokens exhausted',
    notEligible: 'Not eligible for trial',
    errorMessage: 'Error Message',
    reason: 'Reason',
    solution: 'Solution',
    registerLogin: 'Register and login to get official account with full features',
    immediateRegister: 'Register Now',
    conversationsNotSaved: 'Conversations will not be saved',
    experienceOnly: 'For experience only',
    loginForPermanentSave: 'Login for permanent save',
    hours: 'hours',
    minutes: 'minutes',
    unlimited: 'Unlimited',
    freeQuota: 'Free Quota',
    tokens: 'Tokens',
    pro: 'Pro',

    // Banner specific texts
    betaStage: 'Beta Stage',
    betaInProgress: 'Beta in Progress',
    productInBeta: 'Product in Beta...',
    joinWaitlist: 'Join Waitlist',
    joinWaitlistFull: 'Join Waitlist',
    welcomeJoinWaitlist: 'Welcome to join the waitlist for priority access!',
    freeTrialActive: 'Free Trial Active',
    freeTrialNotAvailable: 'Free Trial Unavailable',
    trialEndedShort: 'Trial Ended',
    freeTrialEndedFull: 'Free Trial Ended',
    tokensUsedUp: 'Tokens Used Up',
    tokensUsedUpDesc: 'Your free trial quota has been fully used',
    trialExpiredDesc: 'Free trial time has ended',
    registerForMore: 'Register Now',
    registerForMoreDesc: 'Get more tokens and permanently save conversation history',
    fullFeatureExperience: 'Full Feature Experience',
    fullFeatureExperienceDesc: 'Unlock all advanced translation features',
    remainingTokens: 'Remaining Tokens',
    remainingTime: 'Remaining Time',
    freeTrialNotice: 'Free Trial Notice',
    conversationsNotSavedDesc: 'Conversation history will be lost after leaving the translation page',
    experienceOnlyDesc: 'You can experience the full effect of real-time translation features',
    loginForPermanentSaveDesc: 'After registration and login, all conversation history will be permanently saved to your account',
    deviceUsedTrialExpired: 'Your device has already used the free trial and it has expired',
    deviceUsedTrialExhausted: 'Your device has already used the free trial and tokens are exhausted',
  },

  // Authentication
  auth: {
    // Common
    login: 'Login',
    logout: 'Logout',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    callback: 'Auth Callback',
    forgotPassword: 'Forgot Password',
    rememberMe: 'Remember Me',
    loginSuccess: 'Login successful',
    loginError: 'Login failed',
    logoutSuccess: 'Logout successful',

    // Login form
    welcomeBack: 'Welcome back',
    loginWithSocialOrEmail: 'Login with your social account or email',
    emailPlaceholder: 'm@example.com',
    forgotPasswordLink: 'Forgot your password?',
    loggingIn: 'Logging in...',
    dontHaveAccount: 'Don\'t have an account?',
    signUp: 'Sign up',

    // Register form
    createAccount: 'Create account',
    verifyYourEmail: 'Verify your email',
    signUpWithSocialOrEmail: 'Sign up with your Apple or Google account',
    enterVerificationCode: 'Enter the verification code sent to your email',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Your full name',
    passwordPlaceholder: 'At least 8 characters',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm your password',
    invitationCode: 'Invitation Code',
    invitationCodeRequired: 'Invitation Code',
    invitationCodePlaceholder: 'Enter 4-character invitation code',
    invitationCodeDescription: 'Case-sensitive 4-character code from your invitation email',
    verificationCode: 'Verification Code',
    verificationCodePlaceholder: '6-digit code',
    codeSentTo: 'Code sent to {email}',
    resendVerificationCode: 'Resend verification code',
    sendVerificationCode: 'Send verification code',
    createAccountButton: 'Create account',
    sendingCode: 'Sending code...',
    creatingAccount: 'Creating account...',
    alreadyHaveAccount: 'Already have an account?',
    signIn: 'Sign in',
    backToPreviousStep: 'Back to previous step',

    // Forgot password form
    resetPassword: 'Reset password',
    setNewPassword: 'Set new password',
    enterEmailForReset: 'Enter your email address and we\'ll send you a reset code',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'At least 8 characters',
    confirmNewPassword: 'Confirm New Password',
    confirmNewPasswordPlaceholder: 'Confirm your new password',
    sendingCodeLoading: 'Sending code...',
    verifying: 'Verifying...',
    resettingPassword: 'Resetting password...',
    verifyCode: 'Verify code',
    resetPasswordButton: 'Reset password',
    rememberPassword: 'Remember your password?',
    backToLogin: 'Back to login',

    // MFA Two-factor authentication
    verificationCodeMFA: 'Verification Code',
    sendCode: 'Send Code',
    sending: 'Sending...',
    resendCode: 'Resend Code',
    clickSendCodeDescription: 'Click "Send Code" to receive a verification code via email',

    // Social login
    orContinueWith: 'Or continue with',
    orContinueWithEmail: 'Or continue with email',
    apple: 'Apple',
    google: 'Google',

    // Error messages
    pleaseEnterEmail: 'Please enter your email address',
    pleaseEnterEmailFirst: 'Please enter your email address first',
    pleaseEnterVerificationCode: 'Please enter the verification code',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    invitationCodeRequiredError: 'Invitation code is required during beta testing',
    failedToSendCode: 'Failed to send verification code',
    invalidVerificationCode: 'Invalid verification code',
    passwordResetFailed: 'Password reset failed',
    registrationFailed: 'Registration failed, please try again',
    mfaRequired: 'Please enter your two-factor authentication code',
    userDoesNotExist: 'User does not exist',
    invalidCredentials: 'Invalid email or password',

    // Success messages
    verificationCodeSent: 'Verification code sent to your email',
    passwordResetCodeSent: 'Password reset code sent to your email',
    codeVerified: 'Code verified, please set your new password',
    passwordResetSuccessful: 'Password reset successful! Please login with your new password',
    registrationSuccessful: 'Registration successful! Redirecting...',
    mfaCodeSent: 'MFA code sent to your email',

    // Legal terms
    termsAndPrivacy: 'By clicking continue, you agree to our',
    termsOfService: 'Terms of Service',
    and: 'and',
    privacyPolicy: 'Privacy Policy',
  },

  // Chat/Conversation
  chat: {
    conversations: 'Conversations',
    noConversations: 'No conversation history',
    loadingConversations: 'Loading conversations...',
    conversationHistory: 'Conversation History',
    backToList: 'Back to List',
    totalTurns: '{count} total turns',
    loadedTurns: '{count} loaded',
    noContent: 'This conversation has no content',
    loadingConversation: 'Loading conversation...',
    sessionTitle: 'Session Title',
    createdAt: 'Created At',
    loadMore: 'Load More',
    remainingTurns: '{count} remaining',
    allLoaded: 'All Loaded',
    allLoadedMessage: 'All {count} conversations loaded',
    browseHistory: 'Browse and view conversation history',
    totalSessions: '{count} total sessions',
    startNewConversation: 'Start a new conversation',
    deleteSession: 'Delete Session',
    confirmDeleteSession: 'Confirm Delete Session',
    deleteSessionWarning: 'Are you sure you want to delete session "{title}"?\nThis action cannot be undone and will permanently delete the session and all its conversation records.',
    confirmDelete: 'Confirm Delete',
    turnsCount: '{count} turns',
    preview: 'Preview {count}',
  },

  // Purchase/Payment
  purchase: {
    title: 'Purchase',
    buyTokens: 'Buy Tokens',
    orderHistory: 'Order History',
    noOrders: 'No order history',
    loadingOrders: 'Loading orders...',
    paymentMethod: 'Payment Method',
    amount: 'Amount',
    status: 'Status',
    date: 'Date',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',

    // Purchase page
    subscriptionPlans: 'Subscription Plans',
    subscriptionDescription: 'Choose a monthly subscription for unlimited access to all features with premium support',
    subscribeNow: 'Subscribe Now',
    tokenPacks: 'Token Packs',
    tokenPacksDescription: 'Purchase token packs for pay-as-you-go usage without monthly commitment',
    buyNow: 'Buy Now',
    oneTimePayment: 'One-time Payment',
    popular: 'Popular',
    recommended: 'Recommended',
    perMonth: '/month',
    tokens: 'Tokens',
    features: 'Features:',

    // Token pack features
    tokensIncluded: 'tokens included',
    noExpirationDate: 'No expiration date',
    payOnceUseAnytime: 'Pay once, use anytime',
    perfectForOccasionalUsers: 'Perfect for occasional users',

    // Payment method selection
    selectPaymentMethod: 'Select Payment Method',
    selectPaymentDescription: 'Choose your preferred payment method to complete the purchase securely.',
    alipayName: 'Alipay (支付宝)',
    alipayDescription: 'Secure payment via Alipay QR code - Fast and reliable',
    polarName: 'Polar.sh',
    polarDescription: 'International payment platform - Global accessibility',
    creatingOrder: 'Creating order...',
    cancel: 'Cancel',

    // Payment processing
    scanToPay: 'Scan to Pay',
    completePayment: 'Complete Payment',
    alipayPaymentDescription: 'Scan the QR code with your Alipay app to complete the payment securely.',
    polarPaymentDescription: 'Complete your payment in the opened window. We\'ll automatically detect when payment is completed.',
    paymentWindowOpened: 'Payment Window Opened',
    completePaymentInNewWindow: 'Complete payment in the new window',
    openInBrowser: 'Open in Browser',
    reopenPaymentWindow: 'Reopen Payment Window',
    checkingStatus: 'Checking Status...',
    checkPaymentStatus: 'Check Payment Status',
    close: 'Close',
    orderId: 'Order ID',

    // Payment status
    waitingForPayment: 'Waiting for payment...',
    paymentReceived: 'Payment received, processing...',
    paymentSuccessful: 'Payment successful!',
    paymentFailed: 'Payment failed. Please try again.',
    paymentExpired: 'Payment expired. Please create a new order.',
  },

  // Admin
  admin: {
    title: 'Admin Panel',
  },

  // Debug/Development
  debug: {
    debugPanel: 'Debug Panel',
    testEvents: 'Test Events',
    parseEvents: 'Parse Events',
    clearEvents: 'Clear Test Events',
    clearAll: 'Clear All',
    eventData: 'Event JSON Data',
    parseError: 'Parse Error',
    testAndParse: 'Test and Parse',
    inputPlaceholder: 'Enter SessionEvent[] JSON data...',
  },

  // UI Components
  ui: {
    swap: 'Swap',
    siderbarTitle: 'Main Features',
    selectLanguageDescription: 'Select application interface language',
    translationLanguageSettings: 'Translation Language Settings',
    comingSoon: 'Feature coming soon...',
    audioSettingsDescription: 'Configure microphone and speaker settings',
    accountSettingsDescription: 'Manage user account and profile',
    microphoneAccessDenied: 'Microphone access denied',
    microphoneInstructions: 'Please allow microphone access in browser settings, then refresh the page',
    refreshPage: 'Refresh Page',
    openSettings: 'Settings',
    dismiss: 'Dismiss',
    microphonePermissionSteps: 'Please follow these steps to enable microphone permission:',
    chromeInstructions: 'Click the lock icon in the address bar → Select "Site settings" → Allow microphone access',
    firefoxInstructions: 'Click the shield icon in the address bar → Select "Permissions" → Allow microphone access',
    safariInstructions: 'Click Safari menu → Preferences → Websites → Microphone → Allow this website access',
    genericInstructions: 'Please allow this website to access microphone in browser settings',
    settingsCompleteRefresh: 'Please refresh the page after completing settings.',
    iosPermissionTitle: 'iOS Microphone Permission Setup',
    iosPermissionDescription: 'iOS requires you to re-authorize microphone permission for each visit. Please follow these steps:',
    iosPermissionTip: 'This is an iOS security feature that requires re-authorization for each new session. For a better experience, consider adding this website to your home screen as a Web App.',
    iosHelp: 'iOS Help',
    retryPermission: 'Retry',
    openSystemSettings: 'Open Settings',
  },

  // Conversation
  conversation: {
    noContent: 'No conversation content',
    startSpeaking: 'Start speaking, auto translate',
    autoTranslate: 'Auto translate',
    turnsCount: '{count} conversations',
    tokens: 'tokens',
    totalCost: 'Total cost',
    translation: 'Translation',
    userA: 'User A',
    userB: 'User B',
    cancelled: 'Cancelled',
    translationIncomplete: 'Translation incomplete',
  },
};
